import { useTranslation } from 'react-i18next';
import { t as translate } from 'i18n';
import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

// MUI's stock placeholder (0.42 light / 0.5 dark) sits at ~2.7:1 — fine for a
// throwaway hint, too faint for a value the reader is meant to take in. 0.6
// clears 4.5:1 in both themes while still reading at half the strength of
// typed text (10:1), which is the whole point: saved, not entered.
const PLACEHOLDER_SX = { '& input::placeholder': { opacity: 0.6 } };

/** A closed list's field — an array whose items name their options — shown as chips. */
const isChipsField = (prop) => prop?.type === 'array' && Array.isArray(prop.items?.enum);

/** An option's label from the list's `enumNames`, or the option itself. */
function enumLabel(prop, option) {
  const names = Array.isArray(prop.items.enumNames) ? prop.items.enumNames : [];
  return names[prop.items.enum.indexOf(option)] || String(option);
}

/**
 * A saved list's values. It arrives as an array or as the comma string a
 * finalizer flattened it to ("event,installation").
 */
const enumItems = (raw) =>
  (Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(',') : [])
    .map((item) => String(item).trim())
    .filter(Boolean);

/**
 * A stored value as display text — shared with the read-only config card.
 * Given its schema `prop`, a closed list reads as its labels, in the saved
 * order; a value the list does not offer reads as itself.
 */
export function formatConfigValue(value, prop) {
  if (value === null || value === undefined) return '';
  if (isChipsField(prop) && (Array.isArray(value) || typeof value === 'string')) {
    return enumItems(value).map((item) => enumLabel(prop, item)).join(', ');
  }
  if (typeof value === 'boolean') return value ? translate('shell.yes') : translate('shell.no');
  if (Array.isArray(value)) return value.map((item) => formatConfigValue(item)).filter(Boolean).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** A closed list's selection, in the list's own order; a value it does not offer is dropped. */
function enumSelection(raw, options) {
  const picked = new Set(enumItems(raw));
  return options.filter((option) => picked.has(option));
}

/**
 * The required fields left without a value, as { [field]: message } — the
 * check both connection pages run before sending.
 *
 * A new connection needs each one, except a chips field left on its schema
 * default: the form shows that default and the server applies it to an
 * absent field. An edit (`editing`) sends only the touched fields, and the
 * API merge reads an absent or blank one as "keep the saved value" — but an
 * emptied list replaces it, so that is the one thing missing there.
 */
export function requiredErrors(schema, config, { editing = false } = {}) {
  const errors = {};
  (schema?.required || []).forEach((key) => {
    const value = config[key];
    const prop = schema.properties?.[key] || {};
    const emptied = Array.isArray(value) && value.length === 0;
    const onDefault = value === undefined && isChipsField(prop) && prop.default?.length > 0;
    const blank = !value && value !== 0 && value !== false;
    if (emptied || (!editing && !onDefault && blank)) errors[key] = translate('data.thisFieldIsRequired');
  });
  return errors;
}

/**
 * Renders a form from a JSON Schema (draft-7 subset).
 *
 * Props:
 * Field order comes from `schema['x-order']` when the schema declares it.
 * Without it the order is whatever the object yields, and a schema stored in a
 * jsonb column yields its keys sorted by length — which puts `tables` above
 * `dataset` and the credential in the middle of the form. Keys the array does
 * not mention keep their existing relative order, after the ones it does.
 *
 * Props:
 *   schema      — JSON Schema object
 *   value       — { [fieldName]: any }
 *   onChange    — (newValue: object) => void
 *   errors      — { [fieldName]: string } (optional validation errors)
 *   configuredFields — string[] (fields already saved; the ones with no value
 *                   to show say "unchanged" instead)
 *   currentValues — { [fieldName]: any } saved values of the NON-CREDENTIAL
 *                   fields (the API's visible_config). Shown as each field's
 *                   placeholder, never as its value — see below.
 */
function orderedEntries(properties, order) {
  const entries = Object.entries(properties);
  if (!Array.isArray(order) || order.length === 0) return entries;
  const rank = new Map(order.map((key, index) => [key, index]));
  const place = (key) => (rank.has(key) ? rank.get(key) : order.length);
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => place(a.entry[0]) - place(b.entry[0]) || a.index - b.index)
    .map(({ entry }) => entry);
}

export default function JsonSchemaForm({
  schema = {},
  value = {},
  onChange,
  errors = {},
  configuredFields = [],
  currentValues = {},
}) {
  const { t: translate } = useTranslation();
  const [showPassword, setShowPassword] = useState({});

  if (!schema.properties) return null;

  const properties = schema.properties;
  const required = schema.required || [];

  const handleChange = (key, newVal) => {
    onChange({ ...value, [key]: newVal });
  };

  const toggleShow = (key) =>
    setShowPassword((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {orderedEntries(properties, schema['x-order']).map(([key, prop]) => {
        const fieldLabel = prop.title || key;
        const isRequired = required.includes(key);
        const isConfigured = configuredFields.includes(key);
        const fieldError = errors[key];
        const currentValue = value[key] ?? '';
        const stored = currentValues[key];
        const storedText = formatConfigValue(stored);
        // A saved value is shown as the PLACEHOLDER, never prefilled: the API
        // merge reads an omitted or blank field as "keep this one", so a
        // prefilled box would resend every value the user never touched —
        // and there'd be no way to tell a real edit from a stale echo.
        // A saved field with nothing to show (a credential) says so instead.
        const placeholder = storedText || (isConfigured ? translate('shell.unchanged') : undefined);
        // MUI hides a placeholder behind an un-shrunk label (they occupy the
        // same line), so a field that has one must float its label up front.
        const labelProps = placeholder ? { shrink: true } : undefined;

        // ── Boolean → Switch ─────────────────────────────────────────────
        if (prop.type === 'boolean') {
          // No placeholder on a switch — an untouched one has to sit at the
          // saved position, or the form would show every toggle as "off".
          const touched = value[key] !== undefined;
          const shown = touched ? currentValue : stored;
          return (
            <Box key={key}>
              <FormControlLabel
                control={
                  <Switch
                    checked={shown === true || shown === 'true'}
                    onChange={(e) => handleChange(key, e.target.checked)}
                  />
                }
                label={fieldLabel}
              />
              {prop.description && (
                <FormHelperText sx={{ ml: 0 }}>{prop.description}</FormHelperText>
              )}
            </Box>
          );
        }

        // ── Array of a closed list → chips ───────────────────────────────
        if (isChipsField(prop)) {
          // Like the switch: an untouched field shows the saved selection —
          // or, with nothing saved, the schema default — never a placeholder.
          // It still sends nothing until touched; the server applies the
          // same default to an absent field.
          const options = prop.items.enum;
          const labelOf = (option) => enumLabel(prop, option);
          const touched = value[key] !== undefined;
          const shown = enumSelection(touched ? value[key] : (stored ?? prop.default ?? []), options);
          return (
            <Autocomplete
              key={key}
              multiple
              disableCloseOnSelect
              options={options}
              value={shown}
              getOptionLabel={labelOf}
              // Sent in the list's order, whatever order they were picked in.
              onChange={(_e, next) => handleChange(key, options.filter((option) => next.includes(option)))}
              renderTags={(items, getTagProps) =>
                items.map((item, index) => {
                  const { key: tagKey, ...tagProps } = getTagProps({ index });
                  return <Chip key={tagKey} {...tagProps} label={labelOf(item)} size="small" />;
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={fieldLabel}
                  required={isRequired}
                  helperText={fieldError || prop.description}
                  error={!!fieldError}
                  inputProps={{ ...params.inputProps, 'aria-label': fieldLabel }}
                />
              )}
            />
          );
        }

        // ── Enum → Select ────────────────────────────────────────────────
        if (prop.enum) {
          // Also no placeholder: a closed list selects the saved option.
          return (
            <TextField
              key={key}
              select
              label={fieldLabel}
              value={currentValue || stored || prop.default || ''}
              onChange={(e) => handleChange(key, e.target.value)}
              required={isRequired}
              helperText={fieldError || prop.description}
              error={!!fieldError}
              fullWidth
            >
              {prop.enum.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          );
        }

        // ── Password string ──────────────────────────────────────────────
        if (prop.format === 'password') {
          const isShowing = showPassword[key];
          return (
            <TextField
              key={key}
              label={fieldLabel}
              type={isShowing ? 'text' : 'password'}
              value={currentValue}
              onChange={(e) => handleChange(key, e.target.value)}
              required={isRequired && !isConfigured}
              helperText={fieldError || prop.description}
              error={!!fieldError}
              fullWidth
              placeholder={placeholder}
              InputLabelProps={labelProps}
              sx={PLACEHOLDER_SX}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => toggleShow(key)} edge="end" aria-label={translate(isShowing ? 'shell.hidePassword' : 'shell.showPassword')}>
                      {isShowing ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          );
        }

        // ── Integer / Number ─────────────────────────────────────────────
        if (prop.type === 'integer' || prop.type === 'number') {
          return (
            <TextField
              key={key}
              label={fieldLabel}
              type="number"
              // The schema default only fills a field with nothing saved —
              // over a saved value it would read as the current setting.
              value={currentValue !== '' ? currentValue : (placeholder ? '' : (prop.default ?? ''))}
              onChange={(e) =>
                handleChange(key, e.target.value === '' ? '' : Number(e.target.value))
              }
              required={isRequired}
              helperText={fieldError || prop.description}
              error={!!fieldError}
              fullWidth
              placeholder={placeholder}
              InputLabelProps={labelProps}
              sx={PLACEHOLDER_SX}
            />
          );
        }

        // ── Plain string ─────────────────────────────────────────────────
        return (
          <TextField
            key={key}
            label={fieldLabel}
            value={currentValue}
            onChange={(e) => handleChange(key, e.target.value)}
            required={isRequired}
            helperText={fieldError || prop.description}
            error={!!fieldError}
            fullWidth
            placeholder={placeholder}
            InputLabelProps={labelProps}
            sx={PLACEHOLDER_SX}
          />
        );
      })}
    </Box>
  );
}
